import { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from 'state/axios';

const BATCH_SIZE = 500;
const CONCURRENT = 3;
const MAX_RETRIES = 3;

/**
 * Shared save logic for crewPayroll pages.
 * @returns {{ isSaving, saveProgress, handleSave, handleCancelSave }}
 *
 * handleSave(dataToSave, dataType, startDate, endDate)
 */
export default function useCrewPayrollSave() {
    const [isSaving, setIsSaving]         = useState(false);
    const [saveProgress, setSaveProgress] = useState(null);
    const abortRef = useRef(false);

    const handleCancelSave = useCallback(() => {
        abortRef.current = true;
        setIsSaving(false);
        setSaveProgress(null);
    }, []);

    const handleSave = useCallback(async (dataToSave, dataType, startDate, endDate) => {
        if (!Array.isArray(dataToSave) || dataToSave.length === 0) {
            toast.error("No data to save!");
            return;
        }

        // Build 1-indexed batches upfront (needed for totalBatch in init)
        const batches = [];
        for (let i = 0; i < dataToSave.length; i += BATCH_SIZE) {
            batches.push(dataToSave.slice(i, i + BATCH_SIZE));
        }
        const totalBatch = batches.length;

        abortRef.current = false;
        setIsSaving(true);
        setSaveProgress(0);

        try {
            // Step 1: Init - create log, get logId
            const initRes = await api.post('/crewpayrollrecords/init', {
                startDate,
                endDate,
                dataType,
                totalBatch,
            });
            const { logId } = initRes.data;

            const batchPayload = (batchNo) => ({
                logId,
                batchNo,
                records: batches[batchNo - 1],
            });

            // Step 2: Upload batches in parallel groups
            let completedBatches = 0;
            for (let i = 0; i < totalBatch; i += CONCURRENT) {
                if (abortRef.current) throw new Error('cancelled');

                const group = [];
                for (let j = i; j < Math.min(i + CONCURRENT, totalBatch); j++) {
                    group.push(j + 1);
                }

                await Promise.all(
                    group.map(batchNo =>
                        api.post('/crewpayrollrecords/batch', batchPayload(batchNo))
                    )
                );

                completedBatches += group.length;
                setSaveProgress(Math.round((completedBatches / totalBatch) * 88));
            }

            if (abortRef.current) throw new Error('cancelled');

            // Step 3: Finalize - server verifies all batches ACKed, retries missing
            setSaveProgress(90);
            let finalRes;
            let retryCount = 0;
            do {
                finalRes = await api.post('/crewpayrollrecords/finalize', { logId });

                if (!finalRes.data.success && finalRes.data.missingBatches?.length > 0) {
                    if (retryCount >= MAX_RETRIES) {
                        throw new Error(
                            `Failed after ${MAX_RETRIES} retries. Missing: ${finalRes.data.missingBatches.join(', ')}`
                        );
                    }
                    await Promise.all(
                        finalRes.data.missingBatches.map(batchNo =>
                            api.post('/crewpayrollrecords/batch', batchPayload(batchNo))
                        )
                    );
                    retryCount++;
                    setSaveProgress(90 + retryCount * 2);
                }
            } while (!finalRes.data.success && retryCount <= MAX_RETRIES);

            setSaveProgress(100);
            toast.success(`Saved ${totalBatch} batch(es) - ${dataToSave.length} records successfully.`);
        } catch (error) {
            if (error.message === 'cancelled') {
                toast('Save cancelled.', { icon: '⚠️' });
            } else {
                toast.error(error?.response?.data?.message || error?.message || "Failed to save!");
            }
        } finally {
            setIsSaving(false);
            setSaveProgress(null);
        }
    }, []);

    return { isSaving, saveProgress, handleSave, handleCancelSave };
}
